import React from 'react';
import {
  Box,
  Icon,
  IconButton,
  Typography,
  Modal,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type {
  ICommonModalMaxWidth,
  ICommonModalProps,
} from '@/components/customModal/interface/customModal.interface';

const MODAL_MAX_WIDTH_MAP: ICommonModalMaxWidth = {
  xs: 444,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

const useStyles = (theme: Theme) => ({
  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: theme.palette.custom?.gray400 ?? theme.palette.grey[400],
    '&:hover': {
      color: theme.palette.custom?.gray700 ?? theme.palette.grey[700],
    },
  },

  centering: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    pointerEvents: 'none',
  },
  wrapper: {
    position: 'relative',
    width: '100%',
    maxWidth: 600,
    maxHeight: 'min(90vh, calc(100dvh - 48px))',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    outline: 'none',
    pointerEvents: 'auto',
  },
  header: {
    position: 'relative',
    flexShrink: 0,
    px: 3,
    py: 2,
    pr: 6,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    px: 3,
    py: 2,
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
});

const CommonModal = ({
  open,
  title,
  onClose,
  children,
  maxWidth = 'sm',
  fullWidth = true,
  disableCloseButton = false,
  disableScrollLock = false,
  disablePortal = false,
}: ICommonModalProps) => {
  const theme = useTheme();
  const sx = useStyles(theme);
  const resolvedMaxWidth = maxWidth ? MODAL_MAX_WIDTH_MAP[maxWidth] : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableScrollLock={disableScrollLock}
      disablePortal={disablePortal}
    >
      <Box sx={sx.centering} tabIndex={-1}>
        <Box
          sx={{
            ...sx.wrapper,
            width: fullWidth ? 'calc(100% - 64px)' : 'auto',
            maxWidth: resolvedMaxWidth,
            borderRadius: 3,
          }}
        >
          {title !== undefined && (
            <Box sx={sx.header}>
              {typeof title === 'string' || typeof title === 'number' ? (
                <Typography variant="h6">{title}</Typography>
              ) : (
                title
              )}

              {!disableCloseButton && (
                <IconButton size="small" onClick={onClose} sx={sx.closeButton}>
                  <Icon>close</Icon>
                </IconButton>
              )}
            </Box>
          )}

          <Box sx={sx.content}>{children}</Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CommonModal;
